import React from "react";
import { FiAlertTriangle, FiLock, FiTool } from "react-icons/fi";
import StaticBackground from "./StaticBackground";

const industryCards = [
  {
    icon: FiAlertTriangle,
    title: "Complex Requirements",
    points: [
      "Unique workflows and systems require deep integration across data sources, processes, and often legacy infrastructure",
      "Generic tools are not designed to handle this",
    ],
  },
  {
    icon: FiLock,
    title: "Costly Blind Spots",
    points: [
      "Critical data locked in silos across systems and processes",
      "Inefficiencies, errors, and missed opportunities go unnoticed",
    ],
  },
  {
    icon: FiTool,
    title: "Fragmented Tools",
    points: [
      "Patchwork software creates integration nightmares — teams spend more time managing tools than doing actual work",
      "Decisions are reactive, slow, and data-poor",
    ],
  },
];

const capabilities = [
  "Computer Vision",
  "IoT & Edge",
  "Predictive AI",
  "Custom Software",
];

const Banner = () => {
  return (
    <>
      <div id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <StaticBackground />
        <div className="relative z-50 w-full max-w-7xl mx-auto px-6 lg:px-20 py-32">
          <div className="flex items-center justify-between gap-12">
            <div className="max-w-2xl">
              <div className="relative mb-8 w-fit">
                <span className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#F07023]" />
                <span className="font-['IBM_Plex_Mono'] text-[#F07023] text-xs tracking-[0.3em] uppercase pl-2">
                  Custom Software Solutions
                </span>
              </div>
              <div className="max-w-3xl">
                <h1 className="text-white text-[clamp(2rem,4vw,3.5rem)] font-[600] leading-tight tracking-tight">
                  Turning Industrial Challenges Into <span className="text-[#F07023]"> Measurable Advantage</span>
                </h1>
                <p className="text-gray-300 text-[clamp(1rem,1.3vw,1.25rem)] mt-6 max-w-2xl leading-relaxed font-[400]">
                  Purpose-built robust solutions for real industrial environments
                </p>
                <p className="text-[#F07023] text-[clamp(0.9rem,1.1vw,1.1rem)] mt-4 font-[500] tracking-wide">
                  From Problem Statement to Production — software customized for your reality
                </p>
              </div>
            </div>

            <div className="hidden lg:flex flex-col gap-3 border border-[#1C2333] p-6 min-w-[200px] flex-shrink-0">
              <div className="font-['IBM_Plex_Mono'] text-xs text-[#8892A4] tracking-widest uppercase mb-1">
                Core Capabilities
              </div>
              {capabilities.map((cap) => (
                <div key={cap} className="flex items-center gap-2 border-t border-[#1C2333] pt-3">
                  <span className="w-1 h-1 bg-[#F07023] flex-shrink-0" />
                  <span className="font-['IBM_Plex_Mono'] text-[#8892A4] text-xs">{cap}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section id="industry-reality" className="py-24 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-['IBM_Plex_Mono'] text-[#F07023] text-xs tracking-[0.3em] uppercase">
              01 / The Challenge
            </span>
            <h2 className="text-[clamp(1.5rem,2.5vw,2.75rem)] font-[700] text-gray-900 mt-4 leading-tight">
              Complex requirements demand purpose-built solutions
            </h2>
            <div className="w-12 h-px bg-[#F07023] mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {industryCards.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="group border-t-2 border-[#F07023]/25 pt-6 transition-colors duration-300 hover:border-[#F07023]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="text-[#F07023] text-xl flex-shrink-0" />
                    <h3 className="text-lg font-[700] text-gray-900">{item.title}</h3>
                  </div>
                  <ul className="space-y-2 pl-7">
                    {item.points.map((point, j) => (
                      <li key={j} className="text-[#8892A4] leading-relaxed text-[0.95rem] flex gap-2">
                        <span className="text-[#F07023] mt-1.5 flex-shrink-0">&#x2022;</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="our-solution" className="bg-[#080C10] py-24 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-['IBM_Plex_Mono'] text-[#F07023] text-xs tracking-[0.3em] uppercase">
            02 / Our Solution
          </span>
          <h2 className="text-white text-[clamp(1.5rem,2.5vw,2.75rem)] font-[700] mt-4 leading-tight">
            Purpose-Built Software For Your Exact Requirements
          </h2>
          <div className="w-12 h-px bg-[#F07023] mx-auto mt-6 mb-8" />
          <p className="text-[#8892A4] text-lg max-w-2xl mx-auto leading-relaxed">
            We don't adapt generic products — we architect solutions from the ground up, engineered precisely to your operational reality.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 border border-[#1C2333]">
            {[
              {
                label: "Production-Grade Code",
                detail: "Written for the scale and reliability your operations demand — not adapted from consumer-grade tooling.",
              },
              {
                label: "Validated Under Real Conditions",
                detail: "Tested against your actual operational constraints, not just a staging environment.",
              },
              {
                label: "Built for Longevity",
                detail: "Documented, maintainable architecture with long-term support — software that outlasts the project.",
              },
            ].map((item) => (
              <div key={item.label} className="bg-[#0D1117] px-6 py-6 text-left flex gap-4">
                <div className="w-0.5 flex-shrink-0 bg-[#F07023] self-stretch" />
                <div>
                  <h4 className="text-white text-sm font-[600] mb-2">{item.label}</h4>
                  <p className="text-[#8892A4] text-xs leading-relaxed">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Banner;
